// Family H — share deep link (RFC-102) with size budget feedback.
import { useMemo, useState } from 'react';
import type { EngineId } from '@geosoft/viewer-sdk';
import { Button, Callout, Card, Field, Stack, Text, tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';
import { buildViewerStateForUrl } from '@/state/buildViewerStateForUrl';
import { encodeView, measureEncodedBytes, URL_PAYLOAD_WARN_BYTES, UrlPayloadTooLargeError } from '@/state/url';
import { useViewerStore } from '@/state/useViewerStore';

function buildAppShareUrl(viewFragment: string) {
  if (typeof window === 'undefined') return viewFragment;
  return `${window.location.origin}${window.location.pathname}${window.location.search}${viewFragment}`;
}

export function ShareViewPanel({ engine }: { engine: EngineId }) {
  const live = useLiveRegion();
  const camera = useViewerStore((s) => s.camera);
  const time = useViewerStore((s) => s.time);
  const visible = useViewerStore((s) => s.visibleLayerIds);
  const saved = useViewerStore((s) => s.savedViews);
  const active = useViewerStore((s) => s.activeScenarioId);
  const compare = useViewerStore((s) => s.compareWithScenarioId);

  const [lastError, setLastError] = useState<string | null>(null);

  const viewerState = useMemo(
    () =>
      buildViewerStateForUrl({
        camera,
        time,
        visibleLayerIds: visible,
        primaryEngine: engine,
        savedViews: saved,
        activeScenarioId: active,
        compareWithScenarioId: compare,
      }),
    [active, camera, compare, engine, saved, time, visible],
  );

  const { encodedEditable, readOnlyUrl, encodeError, warnBytes } = useMemo(() => {
    let editable = '';
    let ro = '';
    let err: string | null = null;
    let warn: number | null = null;
    const measureWarn = (b: number) => {
      if (b > URL_PAYLOAD_WARN_BYTES) warn = b;
    };
    try {
      editable = encodeView(viewerState, { onLargePayload: measureWarn });
    } catch (e) {
      if (e instanceof UrlPayloadTooLargeError) {
        err = `Link exceeds the byte budget (${e.bytes} bytes). Remove layers or scenarios.`;
      } else {
        err = (e as Error).message;
      }
    }
    if (!err) {
      try {
        ro = encodeView(viewerState, { readOnly: true, onLargePayload: measureWarn });
      } catch (e) {
        if (e instanceof UrlPayloadTooLargeError) {
          err = `Link exceeds the byte budget (${e.bytes} bytes). Remove layers or scenarios.`;
        } else {
          err = (e as Error).message;
        }
      }
    }
    return {
      encodedEditable: editable ? buildAppShareUrl(editable) : '',
      readOnlyUrl: ro ? buildAppShareUrl(ro) : '',
      encodeError: err,
      warnBytes: warn,
    };
  }, [viewerState]);

  const sizeBytes = useMemo(() => {
    if (encodeError) return 0;
    return measureEncodedBytes(viewerState);
  }, [encodeError, viewerState]);

  const copy = async (value: string, readOnly: boolean) => {
    if (!value) {
      live.announce('status', 'No link generated.');
      return;
    }
    try {
      setLastError(null);
      await navigator.clipboard.writeText(value);
      live.announce(
        'status',
        readOnly ? 'Read-only view link copied to clipboard.' : 'Editable view link copied to clipboard.',
      );
    } catch {
      setLastError('Unable to access clipboard. Copy the link manually.');
      live.announce('alert', 'Clipboard not available. Copy the link manually.');
    }
  };

  return (
    <Stack direction="column" gap={2} style={{ minWidth: 0 }}>
      <Text variant="body" as="p" style={{ margin: 0 }}>
        Create a link that encodes the camera, time, visible layers, engine, saved scenarios, and org context per RFC-102.
      </Text>

      {warnBytes != null ? (
        <Callout severity="warning" title="Link is large">
          <Text variant="caption" as="p" style={{ margin: 0 }}>
            Payload is {warnBytes} bytes. Consider saving a leaner set of scenarios before sharing.
          </Text>
        </Callout>
      ) : null}

      {sizeBytes > 0 ? (
        <Text variant="caption" as="p" style={{ margin: 0, color: tokens.color.ink.secondary }}>
          Approximate size {sizeBytes} bytes
        </Text>
      ) : null}

      {encodeError ? (
        <Text variant="body" as="p" style={{ margin: 0, color: tokens.color.semantic.danger }}>
          {encodeError}
        </Text>
      ) : null}
      {lastError ? (
        <Text variant="body" as="p" style={{ margin: 0, color: tokens.color.semantic.danger }}>
          {lastError}
        </Text>
      ) : null}

      <Field label="Editable view link">
        {({ id, 'aria-describedby': ad }) => (
        <input
          id={id}
          readOnly
          value={encodeError ? '' : encodedEditable}
          onFocus={(e) => e.currentTarget.select()}
          aria-label="Editable view link"
          aria-describedby={ad}
          style={{
            width: '100%',
            minHeight: tokens.density.touchMin,
            borderRadius: tokens.radius.input,
            border: tokens.border.thin,
            background: tokens.color.surface[0],
            color: tokens.color.ink.primary,
            paddingInline: tokens.space[2],
            fontSize: tokens.font.size.sm,
            fontFamily: tokens.font.family.mono,
          }}
        />
        )}
      </Field>
      <Stack direction="row" align="center" gap={1} style={{ flexWrap: 'wrap' }}>
        <Button
          data-testid="copy-share-link"
          onClick={() => void copy(encodeError ? '' : encodedEditable, false)}
          type="button"
        >
          Copy editable link
        </Button>
        <Button
          variant="secondary"
          onClick={() => void copy(encodeError ? '' : readOnlyUrl, true)}
          type="button"
        >
          Copy read-only link
        </Button>
      </Stack>

      {!encodeError && encodedEditable ? (
        <Card level={1} header="What is included" data-testid="share-what-included">
          <Text variant="caption" as="p" style={{ margin: 0 }}>
            The link embeds the current view center and zoom, timeline pin when set, {visible.size} active layer
            {visible.size === 1 ? '' : 's'}, the active engine, {saved.length} saved scenario{saved.length === 1 ? '' : 's'}{' '}
            for compare, and the tenant org alias.
          </Text>
        </Card>
      ) : null}
    </Stack>
  );
}
